/**
 * @greymass/webauthn v1.0.1
 * https://github.com/greymass/eosio-webauthn
 *
 * @license
 * Copyright (c) 2021 FFF00 Agents AB & Greymass Inc. All Rights Reserved.
 * 
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 * 
 *  1. Redistribution of source code must retain the above copyright notice, this
 *     list of conditions and the following disclaimer.
 * 
 *  2. Redistribution in binary form must reproduce the above copyright notice,
 *     this list of conditions and the following disclaimer in the documentation
 *     and/or other materials provided with the distribution.
 * 
 *  3. Neither the name of the copyright holder nor the names of its contributors
 *     may be used to endorse or promote products derived from this software without
 *     specific prior written permission.
 * 
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 * IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE
 * OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 * 
 * YOU ACKNOWLEDGE THAT THIS SOFTWARE IS NOT DESIGNED, LICENSED OR INTENDED FOR USE
 * IN THE DESIGN, CONSTRUCTION, OPERATION OR MAINTENANCE OF ANY MILITARY FACILITY.
 */
import { ABIEncoder, PublicKey, KeyType, Bytes, Checksum256, Signature } from '@greymass/eosio';
import { decode } from 'cborg';
import { ec } from 'elliptic';

class Decoder {
    constructor(data) {
        this.pos = 0;
        if (data instanceof Uint8Array) {
            this.data = new DataView(data.buffer, data.byteOffset, data.byteLength);
            this.array = data;
        }
        else {
            this.data = new DataView(data);
            this.array = new Uint8Array(data);
        }
    }
    canRead(bytes = 1) {
        return !(this.pos + bytes > this.array.byteLength);
    }
    ensure(bytes) {
        if (!this.canRead(bytes)) {
            throw new Error('Read past end of buffer');
        }
    }
    /** Read one byte. */
    readByte() {
        this.ensure(1);
        return this.array[this.pos++];
    }
    /** Read integer as JavaScript number, up to 32 bits. */
    readNum(byteWidth, littleEndian = false) {
        this.ensure(byteWidth);
        const d = this.data, p = this.pos;
        let rv;
        switch (byteWidth) {
            case 1:
                rv = d.getUint8(p);
                break;
            case 2:
                rv = d.getUint16(p, littleEndian);
                break;
            case 4:
                rv = d.getUint32(p, littleEndian);
                break;
            case -1:
                rv = d.getInt8(p);
                break;
            case -2:
                rv = d.getInt16(p, littleEndian);
                break;
            case -4:
                rv = d.getInt32(p, littleEndian);
                break;
            default:
                throw new Error('Invalid integer width');
        }
        this.pos += byteWidth;
        return rv;
    }
    readArray(length) {
        this.ensure(length);
        const rv = this.array.subarray(this.pos, this.pos + length);
        this.pos += length;
        return rv;
    }
    remainder() {
        return this.array.subarray(this.pos);
    }
    readDer(tag) {
        const t = this.readByte();
        if (t !== tag) {
            throw new Error(`Unexpected DER tag: ${t}`);
        }
        const len = this.readByte();
        if (len < 0) {
            throw new Error('Invalid DER length');
        }
        return this.readArray(len);
    }
    derDecoder(tag) {
        return new Decoder(this.readDer(tag));
    }
}

function createPublic(attestationResponse) {
    const clientData = decodeBinaryJson(attestationResponse.clientDataJSON);
    const origin = clientData.origin;
    if (typeof origin !== 'string') {
        throw new Error('Missing origin in client data');
    }
    const originUrl = new URL(origin);
    if (originUrl.protocol !== 'https:') {
        throw new Error('WebAuthn keys require https');
    }
    const attestationObject = decode(new Uint8Array(attestationResponse.attestationObject));
    if (!(attestationObject.authData instanceof Uint8Array)) {
        throw new Error('Missing auth data');
    }
    const authData = decodeAuthData(attestationObject.authData);
    const ecPoint = getECPoint(authData.credentialPublicKey);
    const compressed = new Uint8Array(33);
    compressed[0] = ecPoint.y[31] & 0x01 ? 0x03 : 0x02;
    compressed.set(ecPoint.x, 1);
    const abiEncoder = new ABIEncoder();
    abiEncoder.writeArray(compressed);
    if (authData.flags & 0x01 /* user present */) {
        abiEncoder.writeByte(0x02);
    }
    else if (authData.flags & 0x04 /* user verified */) {
        abiEncoder.writeByte(0x02);
    }
    else {
        abiEncoder.writeByte(0x00);
    }
    abiEncoder.writeString(originUrl.hostname);
    return new PublicKey(KeyType.WA, abiEncoder.getBytes());
}
function createSignature(publicKey, assertionResponse) {
    const decoder = new Decoder(assertionResponse.signature).derDecoder(0x30);
    const r = fixPoint(decoder.readDer(0x02));
    const s = fixPoint(decoder.readDer(0x02));
    const authenticatorData = Bytes.from(assertionResponse.authenticatorData);
    const clientDataJSON = Bytes.from(assertionResponse.clientDataJSON);
    const message = new Bytes();
    message.append(authenticatorData);
    message.append(Checksum256.hash(clientDataJSON));
    const curve = new ec('p256');
    const pk = curve.keyFromPublic(publicKey.data.array.slice(0, 33)).getPublic();
    const m = Checksum256.hash(message).array;
    const recid = curve.getKeyRecoveryParam(m, { r, s }, pk);
    const encoder = new ABIEncoder();
    encoder.writeByte(recid + 31);
    encoder.writeArray(r);
    encoder.writeArray(s);
    authenticatorData.toABI(encoder);
    clientDataJSON.toABI(encoder);
    return new Signature(KeyType.WA, encoder.getBytes());
}
function decodeAuthData(authData) {
    const decoder = new Decoder(authData);
    const rpIdHash = decoder.readArray(32);
    const flags = decoder.readByte();
    const counter = decoder.readNum(4);
    const aaguid = decoder.readArray(16);
    const credentialId = decoder.readArray(decoder.readNum(2));
    const credentialPublicKey = decode(decoder.remainder(), { useMaps: true });
    return {
        rpIdHash,
        flags,
        counter,
        aaguid,
        credentialId,
        credentialPublicKey,
    };
}
function getECPoint(credentialPublicKey) {
    const kty = credentialPublicKey.get(1);
    if (kty !== 2 /* EC */) {
        throw new Error(`Unsupported key type: ${kty}`);
    }
    const alg = credentialPublicKey.get(3);
    if (alg !== -7 /* EC2 */) {
        throw new Error(`Unsupported key algorithm: ${alg}`);
    }
    const crv = credentialPublicKey.get(-1);
    if (crv !== 1 /*P-256*/) {
        throw new Error(`Unsupported ec key curve: ${crv}`);
    }
    const x = credentialPublicKey.get(-2);
    const y = credentialPublicKey.get(-3);
    if (!(x instanceof Uint8Array) || !(y instanceof Uint8Array)) {
        throw new Error('Invalid public key data');
    }
    return { x, y };
}
function decodeBinaryJson(data) {
    const decoder = new TextDecoder();
    return JSON.parse(decoder.decode(data));
}
// chrome sometimes returns curve points that are not 32 bytes, so we need to make sure they are
function fixPoint(x) {
    if (x.length === 32) {
        return x;
    }
    const rv = new Uint8Array(32);
    rv.fill(0);
    let si = 0;
    while (x[si] === 0 && si < x.length - 1) {
        si++;
    }
    rv.set(x.slice(si), 32 - x.length + si);
    return rv;
}

export { createPublic, createSignature };
//# sourceMappingURL=eosio-webauthn.m.js.map
